import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Loader2, Sparkles, ChevronDown, ChevronUp, Tag, Check, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { PUBLIC_ROUTES } from "@/routes/public";
import { getEventPlan, upgradeEventPlan, type UpgradeEventPlanResponse, type EventPlanResponse } from "@/services/api/eventPlans";
import { getGuestUsageMeter } from "@/utils/eventUtils";
import { createPlanPaymentIntent, confirmPlanPayment } from "@/services/api/payments";
import { getSubscriptionPlans } from "@/services/api/subscriptions";
import { validateVoucherCode, getVoucherRejectionReason, voucherErrorKey, type VoucherValidation } from "@/services/api/publicCampaigns";
import { toast } from "sonner";
import type { PlanType } from "@/models/Plan";
import { UnassignedPurchaseApply } from "@/components/customer/purchases/UnassignedPurchaseApply";
import { TrashRestoreHint } from "@/components/customer/purchases/TrashRestoreHint";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";

/**
 * JUB-52: plan payments charge the PLATFORM account (unlike gift direct
 * charges), so Stripe.js is initialised without a connected account.
 * Lazy import keeps stripe-js script injection off the editor's critical path.
 */
let stripePromise: ReturnType<typeof import("@stripe/stripe-js").loadStripe> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = import("@stripe/stripe-js").then((m) => m.loadStripe(stripePublicKey));
  }
  return stripePromise;
}

interface EventPlanUpgradeProps {
  eventUuid: string;
  /** Shown in the irreversible "apply unassigned plan" confirmation. */
  eventName?: string;
  currentLocale?: string;
  currentPlan?: PlanType;
  onPlanUpdated?: () => void;
  /**
   * JUB-75: confirmed-guest count for the x/50 usage meter, sourced from
   * `event.stats.confirmedGuests` (EventResource) by the caller. That figure
   * is the same one the RSVP guest-limit enforcement checks against
   * (Guest::confirmedPeople(), see PublicPages/RsvpController::submit), and
   * it is kept fresh via Event::invalidateOwnerCache() on every guest write
   * (JUB-129) — so it is safe to display here without a separate fetch.
   */
  confirmedGuests?: number;
}

interface PaymentSession {
  clientSecret: string;
  paymentId: number;
  paymentIntentId: string;
  amount: number;
  currency: string;
}

/**
 * Card entry + confirmation step (JUB-52). Rendered inside <Elements> once a
 * real payment intent exists; confirms the card with Stripe.js and then lets
 * the backend finalize (entitlement grant) via /payments/confirm.
 */
function PlanCardPaymentForm({
  session,
  onSucceeded,
  onCancel,
}: {
  session: PaymentSession;
  onSucceeded: () => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;

    setPaying(true);
    setCardError(null);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(session.clientSecret, {
        payment_method: { card },
      });
      if (error) {
        setCardError(error.message || t("events.plan.paymentFailed"));
        return;
      }
      if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
        const confirmed = await confirmPlanPayment(session.paymentId, session.paymentIntentId);
        if (confirmed.success) {
          await onSucceeded();
          return;
        }
      }
      setCardError(t("events.plan.paymentFailed"));
    } catch (err: unknown) {
      console.error("Plan payment confirmation failed:", err);
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message;
      setCardError(message || t("events.plan.paymentFailed"));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-3 pt-2 border-t" data-testid="plan-card-payment">
      <Label className="text-xs">{t("events.plan.cardDetails")}</Label>
      <div className="rounded-md border bg-background p-3">
        <CardElement options={{ hidePostalCode: true }} onChange={() => setCardError(null)} />
      </div>
      {cardError && <p className="text-xs text-destructive">{cardError}</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="text-xs" onClick={onCancel} disabled={paying}>
          {t("common.cancel")}
        </Button>
        <Button
          size="sm"
          className="text-xs"
          onClick={handleConfirm}
          disabled={!stripe || paying}
          data-testid="confirm-card-payment-button"
        >
          {paying ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              {t("common.loading")}
            </>
          ) : (
            t("events.plan.payAmount", {
              amount: String(session.amount),
              currency: session.currency,
            })
          )}
        </Button>
      </div>
    </div>
  );
}

export function EventPlanUpgrade({ eventUuid, eventName, currentLocale = "ro", currentPlan, onPlanUpdated, confirmedGuests }: EventPlanUpgradeProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState<EventPlanResponse | null>(null);
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeEventPlanResponse | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'standard' | 'premium' | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Voucher state
  const [voucherCode, setVoucherCode] = useState("");
  const [validatedVoucher, setValidatedVoucher] = useState<VoucherValidation | null>(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [discountedAmount, setDiscountedAmount] = useState<number | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [withdrawalWaiverAccepted, setWithdrawalWaiverAccepted] = useState(true);
  // JUB-52: active Stripe payment session (real card entry step)
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>(null);
  // Prices sourced from the public plans endpoint instead of JSX literals,
  // so admin-side pricing changes propagate (council audit finding).
  const [planPrices, setPlanPrices] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    loadPlanInfo();
    getSubscriptionPlans()
      .then(({ plans }) => {
        const prices: Record<string, number> = {};
        for (const [key, p] of Object.entries(plans)) {
          if (p && typeof p.price === "number") prices[key] = p.price;
        }
        setPlanPrices(prices);
      })
      .catch((err) => console.error("Failed to load plan prices:", err));
  }, [eventUuid]);

  const loadPlanInfo = async () => {
    try {
      const info = await getEventPlan(eventUuid);
      setPlanInfo(info);
    } catch (error) {
      console.error('Failed to load plan info:', error);
    }
  };

  const handleUpgradeClick = async (targetPlan: 'standard' | 'premium') => {
    setSelectedPlan(targetPlan);
    setUpgradeInfo(null);
    setVoucherCode("");
    setValidatedVoucher(null);
    setDiscountedAmount(null);

    try {
      setLoading(true);
      const upgradeData = await upgradeEventPlan(eventUuid, targetPlan, false);
      setUpgradeInfo(upgradeData);
    } catch (error: unknown) {
      console.error('Failed to get upgrade info:', error);
      const errorMessage = (error as { message?: string })?.message;
      toast.error(errorMessage || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim() || !selectedPlan) return;

    try {
      setValidatingVoucher(true);
      const validation = await validateVoucherCode(voucherCode.trim(), selectedPlan);
      
      if (validation.valid) {
        setValidatedVoucher(validation);
        
        // Calculate discounted amount
        if (upgradeInfo && typeof upgradeInfo.amount === "number" && validation.discount) {
          const originalAmount = upgradeInfo.amount;
          const discountValue = validation.discount.discount;
          const unit = validation.discount.unit;
          
          let discount = 0;
          if (unit === "percent") {
            discount = originalAmount * (discountValue / 100);
          } else {
            discount = discountValue;
          }
          
          setDiscountedAmount(Math.max(0, originalAmount - discount));
        }
        
        toast.success(t('events.plan.voucherApplied') || 'Voucher applied!');
      }
    } catch (error: unknown) {
      console.error('Failed to validate voucher:', error);
      // JUB-27: prefer the translated per-reason message; fall back to the
      // backend text, then to the generic one.
      const errorMessage = getVoucherRejectionReason(error)
        ? t(voucherErrorKey(error))
        : (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t('events.plan.voucherInvalid') || 'Invalid voucher code');
      setValidatedVoucher(null);
      setDiscountedAmount(null);
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucherCode("");
    setValidatedVoucher(null);
    setDiscountedAmount(null);
  };

  const handlePaymentSucceeded = async () => {
    toast.success(t('events.plan.upgradeSuccess') || 'Plan upgraded successfully!');
    await loadPlanInfo();
    if (onPlanUpdated) {
      onPlanUpdated();
    }
    setUpgradeInfo(null);
    setSelectedPlan(null);
    setPaymentSession(null);
    handleRemoveVoucher();
  };

  const handlePay = async () => {
    if (!upgradeInfo || !selectedPlan) return;
    if (!termsAccepted || !withdrawalWaiverAccepted) {
      toast.error(t('payment.legalConsent.required'));
      return;
    }

    try {
      setLoading(true);

      // Create payment intent with event UUID and voucher code if validated
      const voucherToApply = validatedVoucher?.valid ? voucherCode.trim() : undefined;
      const paymentIntent = await createPlanPaymentIntent(selectedPlan, eventUuid, voucherToApply, {
        termsAccepted,
        withdrawalWaiverAccepted,
      });

      // JUB-223: 100% discount voucher (0 RON) fulfills immediately on the server without Stripe CardElement
      if (paymentIntent.freeViaVoucher || paymentIntent.confirmed || paymentIntent.amount === 0) {
        await handlePaymentSucceeded();
        return;
      }

      // JUB-52: the test endpoint is used ONLY when the backend explicitly
      // says Stripe is unconfigured (testMode) AND we're on a dev build.
      // A missing clientSecret in production is an error, not a silent
      // fallback into fake payments (council audit finding).
      const explicitTestMode = Boolean((paymentIntent as Record<string, unknown>).testMode);
      if (explicitTestMode && import.meta.env.DEV) {
        const { completePlanPaymentTest } = await import('@/services/api/payments');
        const confirmed = await completePlanPaymentTest(
          paymentIntent.paymentId,
          paymentIntent.paymentIntentId
        );
        if (confirmed.success) {
          await handlePaymentSucceeded();
        }
        return;
      }

      if (!paymentIntent.clientSecret) {
        toast.error(t('events.plan.paymentUnavailable'));
        return;
      }

      // Real flow: show the card entry step (Stripe Elements)
      setPaymentSession({
        clientSecret: paymentIntent.clientSecret,
        paymentId: paymentIntent.paymentId,
        paymentIntentId: paymentIntent.paymentIntentId,
        amount: discountedAmount ?? paymentIntent.amount ?? upgradeInfo.amount,
        currency: paymentIntent.currency ?? upgradeInfo.currency,
      });
    } catch (error: unknown) {
      console.error('Payment failed:', error);
      toast.error((error as Error)?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const plan = planInfo?.plan || currentPlan || 'free';
  const planDetails = planInfo?.planDetails;
  // JUB-75: x/50 confirmed-guest usage meter — null (hidden) for unlimited
  // plans (Premium, limit -1) or while planDetails hasn't loaded yet.
  const guestUsage = getGuestUsageMeter(confirmedGuests, planDetails?.limits?.guests);

  return (
    <Card data-testid="event-plan-upgrade">
      <CardHeader data-testid="event-plan-upgrade-header" className="cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-pink-600" />
            <CardTitle className="text-base">
              {t('events.plan.title') || 'Event Plan'}
            </CardTitle>
          </div>
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <CardDescription className="text-xs mt-1">
          {t('events.plan.description') || 'Upgrade your event plan to unlock more features'}
        </CardDescription>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-3 pt-0">
        {/* Current Plan */}
        <div className="flex items-center justify-between p-2 rounded-lg border bg-muted/50">
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground">{t('events.plan.current') || 'Current Plan'}</p>
            <Badge data-testid="current-plan-badge" variant={plan === 'premium' ? 'default' : plan === 'standard' ? 'secondary' : 'outline'} className="text-xs">
              {planDetails?.name || plan.toUpperCase()}
            </Badge>
          </div>
        </div>

        {/* Guest usage meter (JUB-75): x/50 confirmed guests against the plan cap */}
        {guestUsage && (
          <div className="p-2 rounded-lg border bg-muted/50 space-y-1.5" data-testid="guest-usage-meter">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t('events.plan.guestUsageTitle')}</span>
              <span
                className={
                  guestUsage.atLimit
                    ? "font-semibold text-destructive"
                    : guestUsage.nearLimit
                      ? "font-semibold text-amber-600 dark:text-amber-500"
                      : "font-semibold text-foreground"
                }
                data-testid="guest-usage-count"
              >
                {t('events.plan.guestUsageCount', { used: guestUsage.used, limit: guestUsage.limit })}
              </span>
            </div>
            <Progress
              value={guestUsage.percent}
              className={guestUsage.atLimit ? "[&>div]:bg-destructive" : guestUsage.nearLimit ? "[&>div]:bg-amber-500" : undefined}
            />
            {guestUsage.atLimit && (
              <p className="text-xs text-destructive" data-testid="guest-usage-at-limit">
                {t('events.plan.guestUsageAtLimit', { plan: planDetails?.name || plan.toUpperCase() })}
              </p>
            )}
          </div>
        )}

        {/* JUB-157: a recently deleted paid event is restored, not bought again */}
        {plan === 'free' && <TrashRestoreHint currentLocale={currentLocale} />}

        {/* JUB-157: legacy unassigned purchase, applied once with confirmation */}
        {plan !== 'premium' && (
          <UnassignedPurchaseApply
            eventUuid={eventUuid}
            eventName={eventName ?? ''}
            currentPlan={plan}
            onApplied={async () => {
              await loadPlanInfo();
              onPlanUpdated?.();
            }}
          />
        )}

        {/* Upgrade Options */}
        {plan === 'free' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 min-w-[140px]"
              onClick={() => handleUpgradeClick('standard')}
              disabled={loading}
              data-testid="upgrade-standard-button"
            >
              <span className="text-xs">Standard</span>
              <span className="text-xs text-muted-foreground ml-1">{planPrices?.standard != null ? `${planPrices.standard} RON` : '…'}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 min-w-[140px]"
              onClick={() => handleUpgradeClick('premium')}
              disabled={loading}
              data-testid="upgrade-premium-button"
            >
              <span className="text-xs">Premium</span>
              <span className="text-xs text-muted-foreground ml-1">{planPrices?.premium != null ? `${planPrices.premium} RON` : '…'}</span>
            </Button>
          </div>
        )}

        {plan === 'standard' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 min-w-[140px]"
              onClick={() => handleUpgradeClick('premium')}
              disabled={loading}
              data-testid="upgrade-premium-button"
            >
              <span className="text-xs">Premium</span>
              <span className="text-xs text-muted-foreground ml-1">{planPrices?.premium != null && planPrices?.standard != null ? `${planPrices.premium - planPrices.standard} RON` : '…'}</span>
            </Button>
          </div>
        )}

        {/* Payment Confirmation */}
        {upgradeInfo && upgradeInfo.requiresPayment && (
          <div className="p-3 rounded-lg border bg-muted/50 space-y-3">
            <p className="text-xs font-medium">
              {t('events.plan.confirmUpgrade') || 'Confirm Upgrade'}
            </p>
            <div className="text-xs text-muted-foreground">
              <p>{t('events.plan.from')}: {upgradeInfo.upgradeFrom?.toUpperCase() || 'FREE'}</p>
              <p>{t('events.plan.to')}: {upgradeInfo.upgradeTo.toUpperCase()}</p>
              
              {/* Price with discount */}
              {discountedAmount !== null && validatedVoucher?.valid ? (
                <div className="mt-1">
                  <p className="text-muted-foreground line-through">
                    {upgradeInfo.amount} {upgradeInfo.currency}
                  </p>
                  <p className="font-semibold text-green-600">
                    {discountedAmount} {upgradeInfo.currency}
                  </p>
                </div>
              ) : (
                <p data-testid="upgrade-amount" className="font-semibold text-foreground mt-1">
                  {t('events.plan.amountLabel')}: {upgradeInfo.amount} {upgradeInfo.currency}
                </p>
              )}
            </div>

            {/* Voucher Code Input — locked once the card step is active so the
                displayed amount cannot desync from the created intent */}
            {!paymentSession && (
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {t('events.plan.voucherCode') || 'Voucher Code'}
              </Label>
              {validatedVoucher?.valid ? (
                <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-xs text-green-700 dark:text-green-400 flex-1 font-mono">
                    {voucherCode}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={handleRemoveVoucher}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && voucherCode.trim() && !validatingVoucher) {
                        e.preventDefault();
                        handleValidateVoucher();
                      }
                    }}
                    placeholder={t('events.plan.enterVoucher') || 'Enter code...'}
                    className="h-8 text-xs font-mono"
                    disabled={validatingVoucher}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleValidateVoucher}
                    disabled={!voucherCode.trim() || validatingVoucher}
                    className="h-8"
                  >
                    {validatingVoucher ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      t('events.plan.apply') || 'Apply'
                    )}
                  </Button>
                </div>
              )}
            </div>
            )}

            {!paymentSession && (
              <div className="space-y-2 pt-2 border-t" data-testid="upgrade-legal-consents">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="upgrade-terms-checkbox"
                    data-testid="upgrade-terms-checkbox"
                    checked={termsAccepted}
                    onCheckedChange={(v) => setTermsAccepted(v === true)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="upgrade-terms-checkbox" className="text-xs text-muted-foreground font-normal leading-snug cursor-pointer">
                    {t('payment.legalConsent.termsPrefix')}{" "}
                    <Link to={PUBLIC_ROUTES.terms(currentLocale)} target="_blank" className="underline text-foreground">
                      {t('footer.terms')}
                    </Link>{" "}
                    &{" "}
                    <Link to={PUBLIC_ROUTES.privacy(currentLocale)} target="_blank" className="underline text-foreground">
                      {t('footer.privacy')}
                    </Link>
                    .
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="upgrade-withdrawal-waiver-checkbox"
                    data-testid="upgrade-withdrawal-waiver-checkbox"
                    checked={withdrawalWaiverAccepted}
                    onCheckedChange={(v) => setWithdrawalWaiverAccepted(v === true)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="upgrade-withdrawal-waiver-checkbox" className="text-xs text-muted-foreground font-normal leading-snug cursor-pointer">
                    {t('payment.legalConsent.withdrawalWaiver')}
                  </Label>
                </div>
              </div>
            )}

            {paymentSession ? (
              <Elements stripe={getStripe()}>
                <PlanCardPaymentForm
                  session={paymentSession}
                  onSucceeded={handlePaymentSucceeded}
                  onCancel={() => setPaymentSession(null)}
                />
              </Elements>
            ) : (
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    setUpgradeInfo(null);
                    setSelectedPlan(null);
                    handleRemoveVoucher();
                  }}
                  disabled={loading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  size="sm"
                  className="text-xs"
                  onClick={handlePay}
                  disabled={loading || !termsAccepted || !withdrawalWaiverAccepted}
                  data-testid="pay-now-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('events.plan.pay') || 'Pay Now'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {loading && !upgradeInfo && (
          <div className="flex items-center justify-center p-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        </CardContent>
      )}
    </Card>
  );
}

